// Applying a user's own ordering to a list of `{ id, name }` entities.
//
// The preference these read is a list of IDS rather than a per-entity rank, so it
// survives entities coming and going: an id that is no longer present is skipped,
// and an entity the order has never seen keeps its server position at the end
// instead of jumping to the front.
//
// Used by the Work Plan clinic rail and the Calendar's Clinics/Doctors filters.

/** The entities in the user's order, unknown-to-the-order ones trailing. */
export function orderByIds(entities, order) {
  const all = (entities || []).filter((entity) => entity?.id);
  const byId = new Map(all.map((entity) => [entity.id, entity]));
  const ordered = [];
  const placed = new Set();
  for (const rawId of order || []) {
    const id = String(rawId || "").trim();
    if (!id || placed.has(id) || !byId.has(id)) continue;
    placed.add(id);
    ordered.push(byId.get(id));
  }
  for (const entity of all) {
    if (!placed.has(entity.id)) ordered.push(entity);
  }
  return ordered;
}

/**
 * Next order after nudging one entity by `delta` places. Takes the ALREADY-ORDERED
 * entities so the result is a complete, self-contained list — no stale ids, no gaps —
 * and returns the input order untouched when the move would fall off either end.
 */
export function moveIdByDelta(orderedEntities, entityId, delta) {
  const ids = (orderedEntities || []).map((entity) => entity?.id).filter(Boolean);
  const from = ids.indexOf(String(entityId || "").trim());
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return ids;
  const next = [...ids];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

/**
 * Next order after dropping one entity at `toIndex` (the drag-and-drop sibling of
 * moveIdByDelta). The index is the slot the entity lands in AFTER it is lifted out,
 * which is what a drop between two neighbours means; out-of-range indices clamp.
 */
export function moveIdToIndex(orderedEntities, entityId, toIndex) {
  const ids = (orderedEntities || []).map((entity) => entity?.id).filter(Boolean);
  const from = ids.indexOf(String(entityId || "").trim());
  if (from < 0) return ids;
  const to = Math.min(ids.length - 1, Math.max(0, Math.round(Number(toIndex))));
  if (!Number.isFinite(to) || to === from) return ids;
  const next = [...ids];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

/** Ids sorted by display name — what the "A–Z" reset writes. */
export function idsSortedByName(entities) {
  return [...(entities || [])]
    .filter((entity) => entity?.id)
    .sort((a, b) =>
      String(a.name || a.shortName || a.id).localeCompare(String(b.name || b.shortName || b.id)),
    )
    .map((entity) => entity.id);
}
